import { SEKTOR_YAPILANDIRMASI, STANDART_KW, STANDART_TANKLAR, FACTORS, PERFORMANCE_CURVES, ISO_8573_MAP, UNLOAD_POWER_FACTOR_DEFAULT, EXTRA_LOSS, REDUNDANCY_OPTIONS, STANDART_BORULAR_INCH, VSD_CONFIG, calculateVSDSavingRatio, getVSDCostPremium, getVSDPartLoadEfficiency, calculateVSDTargetFlow } from './compressorConfig';

const SAFETY_MARGIN = 1.05;

const findKWForFlowAndPressure = (requiredFlow, pressure) => {
  const pressures = Object.keys(PERFORMANCE_CURVES[7.5]).map(Number);
  const refPressure = pressures.reduce((p, c) => Math.abs(c - pressure) < Math.abs(p - pressure) ? c : p, pressures[0]);
  const flowWithMargin = requiredFlow * SAFETY_MARGIN;
  for (const kw of STANDART_KW) {
    const curve = PERFORMANCE_CURVES[kw];
    if (curve && curve[refPressure] >= flowWithMargin) return kw;
  }
  return STANDART_KW[STANDART_KW.length - 1];
};

const recommendPipeSize = (flowM3dk) => {
  const Q_m3s = flowM3dk / 3600;
  const v_target = 7;
  const area = Q_m3s / v_target;
  const D_m = Math.sqrt(4 * area / Math.PI);
  const D_inch = D_m * 39.37;
  return STANDART_BORULAR_INCH.find(s => s >= D_inch) || STANDART_BORULAR_INCH[STANDART_BORULAR_INCH.length - 1];
};

const calculatePressureDrop = (length, diameterInch, flowM3dk) => {
  if (length <= 0 || diameterInch <= 0) return EXTRA_LOSS.total;
  const pipeDrop = 0.0008 * (length / 100) * Math.pow((flowM3dk * 1000) / Math.pow(diameterInch * 25.4, 3), 1.85);
  return Math.max(0.5, Math.min(3.0, pipeDrop)) + EXTRA_LOSS.total;
};

const getISO8573Class = (cig, filterSet) => {
  const p = ISO_8573_MAP.particle[filterSet] || 3;
  const w = ISO_8573_MAP.water[String(cig)] || 4;
  const o = ISO_8573_MAP.oil[filterSet] || 3;
  return `${p}.${w}.${o}`;
};

export const calculateAdvancedCompressor = (girdiler) => {
  const { 
    sektor, altKategoriKey, calismaBasinci, cigNoktasi, calismaTipi, 
    makineSayisi, makineTuketimM3dk, toplamTuketimM3dk, buyumeOrani, 
    firma, yetkili, elektrikFiyati, calismaSaati = 6000, 
    ortalamaYuk = 75, hatUzunluk = 50, boruCapi = 2, 
    loadRatio = 0.75, unloadPowerFactor = 0.25, cycleTime, 
    kacakOrani = 10, redundancyMode = 'otomatik',
    verimlilikFaktoru = 1.0,
    auditMode = false, existingCapacityM3dk, measuredConsumptionM3dk, currentEnergyCost,
    kompresorTipi = 'otomatik', vsdPremiumOverride, analysisPeriod = 10
  } = girdiler;

  const config = altKategoriKey ? SEKTOR_YAPILANDIRMASI[altKategoriKey] : null;
  const p_calisma = calismaBasinci || config?.basinc || 7.5;
  const targetCig = Number(cigNoktasi) || config?.cig || 3;
  
  const baseM3dk = toplamTuketimM3dk > 0 ? toplamTuketimM3dk : (makineTuketimM3dk * makineSayisi);
  const esZamanlilik = calismaTipi === 'sürekli' ? 0.9 : 0.7;
  const fadM3dk = baseM3dk * esZamanlilik * FACTORS.GUVENLIK * FACTORS.KACAK * (1 + buyumeOrani / 100);

  const t_dk = cycleTime || (calismaTipi === 'sürekli' ? 2 : 10);
  const totalLoss = calculatePressureDrop(hatUzunluk, boruCapi, fadM3dk);
  const p_desarj = p_calisma + totalLoss;

  // ✅ YENİ: VSD için headroom'lu hedef debi hesapla
  const selectedCompressorType = kompresorTipi === 'otomatik' ? (fadM3dk > 6 || loadRatio < 0.7 ? 'vsd' : 'sabit') : kompresorTipi;
  const fadForKWSelection = calculateVSDTargetFlow(fadM3dk, selectedCompressorType);
  
  // kW seçimi: VSD ise headroom'lu debiye göre, sabit hız ise base FAD'e göre
  let unitKW = findKWForFlowAndPressure(
    selectedCompressorType === 'vsd' ? fadForKWSelection : fadM3dk, 
    p_desarj
  );

  let konfigurasyon = { adet: 1, kW_birim: unitKW, toplamkW: unitKW, yedek: false, metin: `1x ${unitKW} kW (Tek Ünite)` };
  if (redundancyMode === 'yedekli' || (redundancyMode === 'otomatik' && fadM3dk > 12)) {
     const halfKW = findKWForFlowAndPressure(
       selectedCompressorType === 'vsd' ? (fadForKWSelection / 2) : (fadM3dk / 2), 
       p_desarj
     );
     konfigurasyon = { adet: 3, kW_birim: halfKW, toplamkW: halfKW * 2, yedek: true, metin: `3x ${halfKW} kW (2 Çalışan + 1 Yedek)` };
  } else if (redundancyMode === 'paralel' || (redundancyMode === 'otomatik' && fadM3dk > 6)) {
     const halfKW = findKWForFlowAndPressure(
       selectedCompressorType === 'vsd' ? (fadForKWSelection / 2) : (fadM3dk / 2), 
       p_desarj
     );
     konfigurasyon = { adet: 2, kW_birim: halfKW, toplamkW: halfKW, yedek: false, metin: `2x ${halfKW} kW (Paralel)` };
  }

  let tankVol_L;
  const Q_Lpm = fadM3dk * 1000 / 60;
  const P_upper = p_desarj * 1.1;
  const P_lower = p_calisma;

  if (calismaTipi === 'sürekli' && fadM3dk > 5) {
    tankVol_L = fadM3dk * 250;
  } else {
    tankVol_L = ((Q_Lpm * t_dk * (P_upper - P_lower)) / P_lower) * FACTORS.SAFETY_FACTOR_TANK;
  }
  
  const tankSecilen = STANDART_TANKLAR.find(t => t >= tankVol_L) || STANDART_TANKLAR[STANDART_TANKLAR.length - 1];

  const isHassas = targetCig <= -20;
  const kurutucuTipi = config?.kurutucu || (isHassas ? 'Adsorpsiyon (-40°C)' : 'Soğutmalı (+3°C)');
  const kurutucuKapasite = fadM3dk * 1.2;
  const filtreSeti = config?.filtre || (isHassas ? 'P+A+S' : 'P+A');

  const unloadFactor = unloadPowerFactor || UNLOAD_POWER_FACTOR_DEFAULT;
  const baseAvgPowerSabit = (loadRatio * konfigurasyon.toplamkW) + ((1 - loadRatio) * konfigurasyon.toplamkW * unloadFactor);
  const avgPowerSabit = baseAvgPowerSabit * verimlilikFaktoru;
  
  const vsdSavingRatio = Math.min(0.45, Math.max(0.05, (1 - loadRatio) * 0.6 + (p_calisma > 8 ? 0.05 : 0)));
  const avgPowerVSD = avgPowerSabit * (1 - vsdSavingRatio);
  const vsdOnerisi = vsdSavingRatio > 0.15 || calismaTipi === 'aralıklı' || buyumeOrani >= 15;

  const leakCostYear = (avgPowerSabit * calismaSaati * (elektrikFiyati || 3.80)) * (kacakOrani / 100);
  const pipeRec = recommendPipeSize(fadM3dk);
  const isoClass = getISO8573Class(targetCig, filtreSeti);

  // ✅ Audit mode
  const audit = auditMode ? {
    existingCapacity: existingCapacityM3dk ? parseFloat(existingCapacityM3dk) : null,
    measuredConsumption: measuredConsumptionM3dk ? parseFloat(measuredConsumptionM3dk) : null,
    theoreticalNeed: fadM3dk,
    gap: existingCapacityM3dk ? (fadM3dk - parseFloat(existingCapacityM3dk)).toFixed(2) : null,
    recommendation: existingCapacityM3dk 
      ? (fadM3dk > parseFloat(existingCapacityM3dk) 
          ? '⚠️ Mevcut sistem yetersiz. Yükseltme veya paralel ekleme önerilir.' 
          : '✅ Mevcut sistem teorik olarak yeterli. Verimlilik ve kaçak analizi önerilir.')
      : null,
    currentAnnualCost: currentEnergyCost ? parseFloat(currentEnergyCost) : null,
    proposedAnnualCost: avgPowerSabit * calismaSaati * (elektrikFiyati || 3.80),
    potentialSavings: currentEnergyCost 
      ? (parseFloat(currentEnergyCost) - (avgPowerSabit * calismaSaati * (elektrikFiyati || 3.80))).toFixed(0)
      : null,
  } : null;

  // ✅ VSD vs Sabit Hız Karşılaştırmalı Hesaplama
  const calculateEnergyComparison = () => {
    const fixedAvgPower = avgPowerSabit;
    const vsdEfficiencyFactor = getVSDPartLoadEfficiency(loadRatio);
    const vsdAvgPower = fixedAvgPower * vsdEfficiencyFactor * verimlilikFaktoru;
    
    const electricityPrice = elektrikFiyati || 3.80;
    const fixedAnnualEnergy = fixedAvgPower * calismaSaati * electricityPrice;
    const vsdAnnualEnergy = vsdAvgPower * calismaSaati * electricityPrice;
    const annualSavings = fixedAnnualEnergy - vsdAnnualEnergy;
    
    const baseEquipmentCost = konfigurasyon.toplamkW * 800;
    const fixedCapex = baseEquipmentCost;
    const vsdCostPremium = vsdPremiumOverride ? parseFloat(vsdPremiumOverride) : getVSDCostPremium(konfigurasyon.toplamkW);
    const vsdCapex = baseEquipmentCost * vsdCostPremium;
    const capexDifference = vsdCapex - fixedCapex;
    
    const simplePaybackYears = annualSavings > 0 ? (capexDifference / annualSavings).toFixed(1) : null;
    
    const { electricityInflation, discountRate } = VSD_CONFIG.roi;
    let npvFixed = -fixedCapex;
    let npvVSD = -vsdCapex;
    
    for (let year = 1; year <= analysisPeriod; year++) {
      const inflationFactor = Math.pow(1 + electricityInflation, year);
      const discountFactor = Math.pow(1 + discountRate, year);
      npvFixed -= (fixedAnnualEnergy * inflationFactor) / discountFactor;
      npvVSD -= (vsdAnnualEnergy * inflationFactor) / discountFactor;
    }
    
    const tcoDifference = npvVSD - npvFixed;
    
    return {
      fixed: {
        avgPower: fixedAvgPower.toFixed(1),
        annualEnergy: fixedAnnualEnergy.toLocaleString('tr-TR'),
        annualCost: fixedAnnualEnergy.toLocaleString('tr-TR'),
        capex: fixedCapex.toLocaleString('tr-TR'),
        npv: npvFixed.toLocaleString('tr-TR')
      },
      vsd: {
        avgPower: vsdAvgPower.toFixed(1),
        annualEnergy: vsdAnnualEnergy.toLocaleString('tr-TR'),
        annualCost: vsdAnnualEnergy.toLocaleString('tr-TR'),
        capex: vsdCapex.toLocaleString('tr-TR'),
        npv: npvVSD.toLocaleString('tr-TR'),
        efficiencyFactor: vsdEfficiencyFactor.toFixed(2),
        costPremium: vsdCostPremium.toFixed(2)
      },
      comparison: {
        powerSavings: (fixedAvgPower - vsdAvgPower).toFixed(1),
        energySavingsPercent: ((1 - vsdAvgPower/fixedAvgPower) * 100).toFixed(1),
        annualSavings: annualSavings.toLocaleString('tr-TR'),
        capexDifference: capexDifference.toLocaleString('tr-TR'),
        simplePayback: simplePaybackYears,
        tcoDifference: tcoDifference.toLocaleString('tr-TR'),
        recommendation: tcoDifference < 0 
          ? '✅ VSD önerilir: 10 yıllık TCO daha düşük' 
          : simplePaybackYears && parseFloat(simplePaybackYears) <= 3
            ? '✅ VSD önerilir: Geri ödeme ≤3 yıl'
            : '⚠️ Sabit hız ekonomik olabilir: Detaylı analiz önerilir'
      }
    };
  };

  const energyComparison = calculateEnergyComparison();
  
  let finalTankSecilen = tankSecilen;
  if (selectedCompressorType === 'vsd') {
    const vsdTankVol = tankVol_L * VSD_CONFIG.tankReductionFactor;
    finalTankSecilen = STANDART_TANKLAR.find(t => t >= vsdTankVol) || STANDART_TANKLAR[STANDART_TANKLAR.length - 1];
  }

  return {
    id: `calc_${Date.now()}`, tarih: new Date().toLocaleString('tr-TR'),
    musteri: { firma: firma || 'Belirtilmedi', yetkili: yetkili || 'Belirtilmedi' },
    paket: { kompresor: konfigurasyon.metin, fad: `${fadM3dk.toFixed(2)} m³/dk`, tank: `${finalTankSecilen} L`, kurutucu: `${kurutucuKapasite.toFixed(2)} m³/dk ${kurutucuTipi}`, filtre: filtreSeti },
    detaylar: { 
      hesaplananFAD_m3dk: fadM3dk.toFixed(2), 
      deşarj: `${p_desarj.toFixed(1)} bar`, 
      hatKaybi: `-${totalLoss.toFixed(1)} bar`, 
      vsd: vsdOnerisi ? '✅ Önerilir' : '❌ Sabit Hız Yeterli', 
      cigHedefi: `${targetCig}°C`, 
      isoKlasi: `ISO 8573-1: Class ${isoClass}`,
      // ✅ YENİ: VSD headroom bilgisi
      vsdHeadroom: selectedCompressorType === 'vsd' ? {
        baseFAD: fadM3dk.toFixed(2),
        targetFAD: fadForKWSelection.toFixed(2),
        headroomPercent: ((fadForKWSelection / fadM3dk - 1) * 100).toFixed(0)
      } : null
    },
    enerji: { 
      ortalamaGucSabit: avgPowerSabit.toFixed(1), 
      ortalamaGucVSD: avgPowerVSD.toFixed(1), 
      sabitYillik: (avgPowerSabit * calismaSaati * (elektrikFiyati || 3.80)).toLocaleString('tr-TR'), 
      vsdYillik: (avgPowerVSD * calismaSaati * (elektrikFiyati || 3.80)).toLocaleString('tr-TR'), 
      tasarruf: ((avgPowerSabit - avgPowerVSD) * calismaSaati * (elektrikFiyati || 3.80)).toLocaleString('tr-TR'), 
      kacakYillikKayip: leakCostYear.toLocaleString('tr-TR'),
      verimlilikNotu: verimlilikFaktoru !== 1.0 ? `Verimlilik faktörü: ${verimlilikFaktoru}` : ''
    },
    tesisat: { onerilenBoruCapi: `${pipeRec}"`, hesaplananKayip: `-${totalLoss.toFixed(1)} bar`, cycleTime: t_dk },
    sektorAnalizi: { 
      sektorAd: config?.ad || 'Genel Uygulama', 
      not: config?.not || 'Standart parametreler kullanıldı.', 
      tavsiye: isHassas ? 'Yüksek hava kalitesi gerektiren uygulama. Adsorpsiyon + aktif karbon zorunludur.' : 'Standart kalite yeterli.' 
    },
    uyariNotlari: [
      p_calisma > 15 ? '⚠️ 15+ bar: PED/ASME sertifikalı tank ve özel seri gerektirir.' : '', 
      fadM3dk > 15 ? '🔗 15 m³/dk+: Merkezi kontrol panosu şart.' : '', 
      loadRatio < 0.4 ? '⚠️ %40 altı yük: VSD veya blower tipi değerlendirilmeli.' : '', 
      kacakOrani > 15 ? '⚠️ Kaçak %15+! Boru ek yerleri ve hızlı bağlantılar kontrol edilmeli.' : '',
      // ✅ YENİ: VSD headroom uyarısı
      (selectedCompressorType === 'vsd' && fadForKWSelection > fadM3dk * 1.05) 
        ? `✅ VSD headroom: ${fadM3dk.toFixed(2)} → ${fadForKWSelection.toFixed(2)} m³/dk (inverter verimlilik bandı için)` 
        : ''
    ].filter(Boolean),
    audit,
    kompresorTipi: selectedCompressorType,
    energyComparison
  };
};